import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Slider } from '@/components/ui/slider';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { 
  Shield, 
  ChevronRight, 
  ExternalLink, 
  CheckCircle2,
  User,
  Mail,
  Send,
  Loader2,
  Users,
  Eye,
  MessageSquare,
  UserCheck,
  Briefcase,
  Star,
  Heart,
  Lightbulb,
  HelpCircle,
  Building2,
  MapPin,
  Globe,
  Target,
  Zap,
  Award,
  Rocket,
  Compass,
  Flag,
  FolderOpen,
  Tag
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Icon mapping by name (matching backend icon names)
const ICON_MAP = {
  'briefcase': Briefcase,
  'star': Star,
  'users': Users,
  'eye': Eye,
  'heart': Heart,
  'message-square': MessageSquare,
  'user-check': UserCheck,
  'lightbulb': Lightbulb,
  'help-circle': HelpCircle,
  'building': Building2,
  'map-pin': MapPin,
  'globe': Globe,
  'target': Target,
  'zap': Zap,
  'award': Award,
  'shield': Shield,
  'rocket': Rocket,
  'compass': Compass,
  'flag': Flag,
  'folder': FolderOpen,
  'tag': Tag,
};

// Get icon component by name with fallback
const getIconByName = (iconName) => {
  if (iconName && ICON_MAP[iconName]) {
    return ICON_MAP[iconName];
  }
  return UserCheck; // Default fallback
};

export default function PublicLandingPage() {
  const { projectId } = useParams();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [landingData, setLandingData] = useState(null);
  const [step, setStep] = useState('welcome'); // welcome, questions, involvement, segmentation, identification, complete
  const [responses, setResponses] = useState({});
  const [selectedInvolvement, setSelectedInvolvement] = useState(null);
  const [selectedSegments, setSelectedSegments] = useState({});
  const [identification, setIdentification] = useState({
    first_name: '',
    last_name: '',
    email: '',
    wants_to_identify: false,
  });
  const [honeypot, setHoneypot] = useState(''); // Bot detection field

  useEffect(() => {
    const fetchLandingData = async () => {
      try {
        const response = await fetch(`${API_URL}/api/public/project/${projectId}/landing`);
        if (!response.ok) throw new Error('Project not found');
        const data = await response.json();
        setLandingData(data);
      } catch (err) {
        toast.error('Failed to load project');
      } finally {
        setLoading(false);
      }
    };
    fetchLandingData();
  }, [projectId]);

  const handleResponseChange = (questionId, value) => {
    setResponses(prev => ({ ...prev, [questionId]: value }));
  };

  const handleSegmentChange = (segmentTypeId, segmentId) => {
    setSelectedSegments(prev => ({ ...prev, [segmentTypeId]: segmentId }));
  };

  // Get all selected segment IDs (involvement + other segments)
  const getAllSelectedSegmentIds = () => {
    const segmentIds = [];
    if (selectedInvolvement) {
      segmentIds.push(selectedInvolvement);
    }
    Object.values(selectedSegments).forEach(id => {
      if (id) segmentIds.push(id);
    });
    return segmentIds;
  };

  // Filter questions based on selected segments
  const getFilteredQuestions = () => {
    if (!landingData?.questions?.length) return [];
    
    const selectedSegmentIds = getAllSelectedSegmentIds();
    
    return landingData.questions.filter(question => {
      // If question has no segment targeting, show to everyone
      if (!question.segment_ids || question.segment_ids.length === 0) {
        return true;
      }
      // If question is targeted to specific segments, check if user selected any of them
      return question.segment_ids.some(segId => selectedSegmentIds.includes(segId));
    });
  };

  // Determine if full segmentation should be shown based on involvement selection
  const shouldShowFullSegmentation = () => {
    if (!landingData?.config?.segmentation_conditions?.length) {
      // No conditions set - always show full segmentation
      return true;
    }
    // Check if selected involvement is in the conditions list
    return landingData.config.segmentation_conditions.includes(selectedInvolvement);
  };

  const getNextSteps = () => {
    const steps = ['welcome'];
    
    // Add involvement step if involvement type exists (comes first)
    if (landingData?.involvement_type?.segments?.length > 0) {
      steps.push('involvement');
    }
    
    // Add segmentation step (comes before questions)
    if (landingData?.config?.allow_self_segmentation && landingData?.segment_types?.length > 0) {
      steps.push('segmentation');
    }
    
    // Add questions step if there are public questions (comes after segmentation)
    if (landingData?.questions?.length > 0) {
      steps.push('questions');
    }
    
    // Add identification step
    if (landingData?.config?.allow_self_identification) {
      steps.push('identification');
    }
    
    steps.push('complete');
    return steps;
  };

  const handleNext = () => {
    const allSteps = getNextSteps();
    const currentIndex = allSteps.indexOf(step);
    
    if (currentIndex < allSteps.length - 1) {
      let nextStep = allSteps[currentIndex + 1];
      
      // If we're leaving involvement step, check if we should skip segmentation
      if (step === 'involvement' && nextStep === 'segmentation') {
        if (!shouldShowFullSegmentation()) {
          // Skip to next step after segmentation
          const segIndex = allSteps.indexOf('segmentation');
          if (segIndex >= 0 && segIndex < allSteps.length - 1) {
            nextStep = allSteps[segIndex + 1];
          }
        }
      }
      
      setStep(nextStep);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      // Submit responses
      if (Object.keys(responses).length > 0) {
        console.log('Submitting responses:', responses);
      }

      // Submit self-identification if requested
      if (identification.wants_to_identify && identification.email) {
        // Check honeypot - if filled, it's likely a bot
        if (honeypot) {
          console.log('Bot detected via honeypot');
          setStep('complete'); // Silently "complete" to not give feedback to bots
          return;
        }
        
        const segmentIds = [
          selectedInvolvement,
          ...Object.values(selectedSegments)
        ].filter(Boolean);
        
        await fetch(`${API_URL}/api/public/project/${projectId}/self-identify`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            first_name: identification.first_name,
            last_name: identification.last_name,
            email: identification.email,
            proposed_segment_ids: segmentIds,
            honeypot: honeypot,
          }),
        });
      }

      setStep('complete');
    } catch (err) {
      toast.error('Failed to submit. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const renderQuestion = (question) => {
    const value = responses[question.id];

    switch (question.question_type) {
      case 'free_text':
        return (
          <Textarea
            value={value || ''}
            onChange={(e) => handleResponseChange(question.id, e.target.value)}
            placeholder="Enter your response..."
            rows={3}
          />
        );

      case 'multiple_choice':
        return (
          <RadioGroup value={value || ''} onValueChange={(v) => handleResponseChange(question.id, v)}>
            {question.options?.map((option, i) => (
              <div key={i} className="flex items-center space-x-2">
                <RadioGroupItem value={option} id={`${question.id}-${i}`} />
                <Label htmlFor={`${question.id}-${i}`}>{option}</Label>
              </div>
            ))}
          </RadioGroup>
        );

      case 'dropdown':
        return (
          <Select value={value || ''} onValueChange={(v) => handleResponseChange(question.id, v)}>
            <SelectTrigger>
              <SelectValue placeholder="Select an option" />
            </SelectTrigger>
            <SelectContent>
              {question.options?.map((option, i) => (
                <SelectItem key={i} value={option}>{option}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        );

      case 'slider':
        const config = question.slider_config || { min_value: 0, max_value: 10 };
        const minVal = config.min_value || 0;
        const maxVal = config.max_value || 10;
        const stepSize = (maxVal - minVal) / 100; // 100 points across the range
        const currentVal = value !== null && value !== undefined ? value : null;
        return (
          <div className="space-y-4">
            <Slider
              value={[currentVal !== null ? currentVal : minVal]}
              min={minVal}
              max={maxVal}
              step={stepSize}
              onValueChange={([v]) => handleResponseChange(question.id, parseFloat(v.toFixed(2)))}
            />
            <div className="flex justify-between text-xs text-slate-500">
              <span>{config.left_label || 'Low'}</span>
              <span>{config.middle_label || ''}</span>
              <span>{config.right_label || 'High'}</span>
            </div>
          </div>
        );

      case 'yes_no':
        return (
          <RadioGroup value={value || ''} onValueChange={(v) => handleResponseChange(question.id, v)}>
            <div className="flex gap-4">
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="yes" id={`${question.id}-yes`} />
                <Label htmlFor={`${question.id}-yes`}>Yes</Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="no" id={`${question.id}-no`} />
                <Label htmlFor={`${question.id}-no`}>No</Label>
              </div>
            </div>
          </RadioGroup>
        );

      case 'rating':
        return (
          <div className="flex gap-2 flex-wrap">
            {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((num) => (
              <Button
                key={num}
                type="button"
                variant={value === num ? 'default' : 'outline'}
                size="sm"
                className={value === num ? 'bg-[#5AA2AE]' : ''}
                onClick={() => handleResponseChange(question.id, num)}
              >
                {num}
              </Button>
            ))}
          </div>
        );

      case 'checkbox':
        const selectedOptions = value || [];
        return (
          <div className="space-y-2">
            {question.options?.map((option, i) => (
              <div key={i} className="flex items-center space-x-2">
                <Checkbox
                  id={`${question.id}-${i}`}
                  checked={selectedOptions.includes(option)}
                  onCheckedChange={(checked) => {
                    const newValue = checked
                      ? [...selectedOptions, option]
                      : selectedOptions.filter(o => o !== option);
                    handleResponseChange(question.id, newValue);
                  }}
                />
                <Label htmlFor={`${question.id}-${i}`}>{option}</Label>
              </div>
            ))}
          </div>
        );

      default:
        return (
          <Input
            value={value || ''}
            onChange={(e) => handleResponseChange(question.id, e.target.value)}
            placeholder="Enter your response..."
          />
        );
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-[#5AA2AE]" />
      </div>
    );
  }

  if (!landingData) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center">
        <Card className="max-w-md">
          <CardContent className="py-8 text-center">
            <h2 className="text-xl font-semibold text-slate-900 mb-2">Project Not Found</h2>
            <p className="text-slate-600">This project doesn't exist or is no longer available.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const { project, config, questions, involvement_type, segment_types } = landingData;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      {/* Hero Section */}
      {config.hero_image_url && (
        <div 
          className="h-48 md:h-64 bg-cover bg-center relative"
          style={{ backgroundImage: `url(${config.hero_image_url})` }}
        >
          <div className="absolute inset-0 bg-black/30" />
        </div>
      )}

      <div className="max-w-2xl mx-auto px-4 py-8">
        {/* Logo */}
        {config.logo_url && (
          <div className="flex justify-center mb-6">
            <img src={config.logo_url} alt="Logo" className="h-16 w-auto object-contain" />
          </div>
        )}

        {/* Welcome Step */}
        {step === 'welcome' && (
          <div className="space-y-6 animate-fade-in">
            <Card className="overflow-hidden">
              <CardHeader className="bg-[#5AA2AE]/5 border-b">
                <CardTitle className="text-2xl text-center" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  {config.welcome_title || `Welcome to ${project.name}`}
                </CardTitle>
              </CardHeader>
              <CardContent className="p-6 space-y-4">
                <p className="text-slate-600 leading-relaxed">
                  {config.welcome_message}
                </p>

                {/* Anonymity Assurance */}
                <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4">
                  <div className="flex items-start gap-3">
                    <Shield className="h-5 w-5 text-emerald-600 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="text-sm font-medium text-emerald-800 mb-1">Your Privacy is Protected</p>
                      <p className="text-sm text-emerald-700">
                        {config.anonymity_message}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Learn More Button */}
                {config.learn_more_url && (
                  <div className="pt-2">
                    <a 
                      href={config.learn_more_url.startsWith('http') ? config.learn_more_url : `https://${config.learn_more_url}`} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="inline-flex items-center text-[#5AA2AE] hover:underline"
                    >
                      Learn More about {project.name}
                      <ExternalLink className="ml-2 h-4 w-4" />
                    </a>
                  </div>
                )}
              </CardContent>
            </Card>

            <Button 
              onClick={handleNext}
              className="w-full bg-[#5AA2AE] hover:bg-[#5AA2AE]/90"
              size="lg"
            >
              Get Started
              <ChevronRight className="ml-2 h-4 w-4" />
            </Button>
          </div>
        )}

        {/* Questions Step */}
        {step === 'questions' && (
          <div className="space-y-6 animate-fade-in">
            {(() => {
              const filteredQuestions = getFilteredQuestions();
              
              if (filteredQuestions.length === 0) {
                // No questions to show, auto-advance to next step
                return (
                  <Card>
                    <CardContent className="py-8 text-center">
                      <CheckCircle2 className="h-12 w-12 text-[#5AA2AE] mx-auto mb-4" />
                      <p className="text-slate-600">No additional questions for your selection.</p>
                      <Button 
                        onClick={handleNext}
                        className="mt-4 bg-[#5AA2AE] hover:bg-[#5AA2AE]/90"
                      >
                        Continue
                        <ChevronRight className="ml-2 h-4 w-4" />
                      </Button>
                    </CardContent>
                  </Card>
                );
              }
              
              return (
                <>
                  <Card>
                    <CardHeader>
                      <CardTitle>Share Your Feedback</CardTitle>
                      <CardDescription>
                        Please answer the following questions. Your input is valuable.
                      </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-6">
                      {filteredQuestions.map((question, index) => (
                        <div key={question.id} className="space-y-3">
                          {question.heading && (
                            <Badge variant="secondary" className="text-xs">
                              {question.heading}
                            </Badge>
                          )}
                          <Label className="text-base font-medium block">
                            {index + 1}. {question.text}
                            {question.required && <span className="text-red-500 ml-1">*</span>}
                          </Label>
                          {renderQuestion(question)}
                        </div>
                      ))}
                    </CardContent>
                  </Card>

                  <Button 
                    onClick={handleNext}
                    className="w-full bg-[#5AA2AE] hover:bg-[#5AA2AE]/90"
                    size="lg"
                  >
                    Continue
                    <ChevronRight className="ml-2 h-4 w-4" />
                  </Button>
                </>
              );
            })()}
          </div>
        )}

        {/* Involvement Step - Card-based selection */}
        {step === 'involvement' && involvement_type && (
          <div className="space-y-6 animate-fade-in">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Users className="h-5 w-5 text-[#5AA2AE]" />
                  {config.involvement_question || 'How would you describe your involvement with the project?'}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid gap-3">
                  {involvement_type.segments?.map((segment) => {
                    // Use segment's icon from DB, or fall back to default
                    const Icon = getIconByName(segment.icon);
                    const isSelected = selectedInvolvement === segment.id;
                    
                    return (
                      <button
                        key={segment.id}
                        type="button"
                        onClick={() => setSelectedInvolvement(segment.id)}
                        data-testid={`involvement-option-${segment.id}`}
                        className={`
                          w-full p-4 rounded-lg border-2 text-left transition-all
                          ${isSelected 
                            ? 'border-[#5AA2AE] bg-[#5AA2AE]/5 ring-2 ring-[#5AA2AE]/20' 
                            : 'border-slate-200 hover:border-[#5AA2AE]/50 hover:bg-slate-50'
                          }
                        `}
                      >
                        <div className="flex items-start gap-4">
                          <div className={`
                            p-2 rounded-lg transition-colors
                            ${isSelected ? 'bg-[#5AA2AE] text-white' : 'bg-slate-100 text-slate-600'}
                          `}>
                            <Icon className="h-5 w-5" />
                          </div>
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <span className={`font-medium ${isSelected ? 'text-[#5AA2AE]' : 'text-slate-900'}`}>
                                {segment.name}
                              </span>
                              {isSelected && (
                                <CheckCircle2 className="h-4 w-4 text-[#5AA2AE]" />
                              )}
                            </div>
                            {segment.description && (
                              <p className="text-sm text-slate-500 mt-1">
                                {segment.description}
                              </p>
                            )}
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </CardContent>
            </Card>

            <Button 
              onClick={handleNext}
              disabled={!selectedInvolvement && config.involvement_required}
              className="w-full bg-[#5AA2AE] hover:bg-[#5AA2AE]/90"
              size="lg"
            >
              Continue
              <ChevronRight className="ml-2 h-4 w-4" />
            </Button>
          </div>
        )}

        {/* Self-Segmentation Step */}
        {step === 'segmentation' && (
          <div className="space-y-6 animate-fade-in">
            <Card>
              <CardHeader>
                <CardTitle>Tell Us More About Yourself</CardTitle>
                <CardDescription>
                  Help us understand your perspective by selecting the options that best describe you.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {segment_types.map((segType) => (
                  <div key={segType.id} className="space-y-2">
                    <Label className="text-base font-medium">{segType.name}</Label>
                    {segType.description && (
                      <p className="text-sm text-slate-500">{segType.description}</p>
                    )}
                    <Select 
                      value={selectedSegments[segType.id] || ''} 
                      onValueChange={(v) => handleSegmentChange(segType.id, v)}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select an option" />
                      </SelectTrigger>
                      <SelectContent>
                        {segType.segments?.map((seg) => (
                          <SelectItem key={seg.id} value={seg.id}>
                            {seg.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                ))}
              </CardContent>
            </Card>

            <Button 
              onClick={handleNext}
              className="w-full bg-[#5AA2AE] hover:bg-[#5AA2AE]/90"
              size="lg"
            >
              Continue
              <ChevronRight className="ml-2 h-4 w-4" />
            </Button>
          </div>
        )}

        {/* Self-Identification Step */}
        {step === 'identification' && (
          <div className="space-y-6 animate-fade-in">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <User className="h-5 w-5 text-[#5AA2AE]" />
                  Would You Like to Identify Yourself?
                </CardTitle>
                <CardDescription>
                  {config.self_id_prompt || 'Optional: Provide your contact details so we can follow up.'}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-start gap-3 p-4 rounded-lg border bg-slate-50">
                  <Checkbox
                    id="wants_to_identify"
                    checked={identification.wants_to_identify}
                    onCheckedChange={(checked) => setIdentification(prev => ({ ...prev, wants_to_identify: checked }))}
                  />
                  <div className="space-y-1">
                    <Label htmlFor="wants_to_identify" className="font-medium cursor-pointer">
                      Yes, I'd like to provide my contact information
                    </Label>
                    <p className="text-sm text-slate-500">
                      This allows the project team to reach out to you for follow-up questions.
                    </p>
                  </div>
                </div>

                {identification.wants_to_identify && (
                  <div className="space-y-4 p-4 border rounded-lg animate-fade-in">
                    {/* Honeypot field - hidden from users, bots will fill it */}
                    <div className="absolute opacity-0 pointer-events-none" aria-hidden="true" tabIndex={-1}>
                      <label htmlFor="website_url">Website</label>
                      <input
                        type="text"
                        id="website_url"
                        name="website_url"
                        value={honeypot}
                        onChange={(e) => setHoneypot(e.target.value)}
                        autoComplete="off"
                        tabIndex={-1}
                      />
                    </div>
                    
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="first_name">First Name</Label>
                        <Input
                          id="first_name"
                          value={identification.first_name}
                          onChange={(e) => setIdentification(prev => ({ ...prev, first_name: e.target.value }))}
                          placeholder="John"
                          data-testid="identification-first-name"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="last_name">Last Name</Label>
                        <Input
                          id="last_name"
                          value={identification.last_name}
                          onChange={(e) => setIdentification(prev => ({ ...prev, last_name: e.target.value }))}
                          placeholder="Doe"
                          data-testid="identification-last-name"
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="email">Email Address</Label>
                      <div className="relative">
                        <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                        <Input
                          id="email"
                          type="email"
                          value={identification.email}
                          onChange={(e) => setIdentification(prev => ({ ...prev, email: e.target.value }))}
                          placeholder="john@example.com"
                          className="pl-10"
                          data-testid="identification-email"
                        />
                      </div>
                      <p className="text-xs text-slate-500">
                        A verification email will be sent to confirm your identity.
                      </p>
                    </div>
                    
                    {/* Simple CAPTCHA */}
                    <div className="p-3 bg-slate-50 rounded-lg border">
                      <p className="text-xs text-slate-500 flex items-center gap-2">
                        <Shield className="h-4 w-4" />
                        Your submission is protected and secured
                      </p>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            <Button 
              onClick={handleSubmit}
              disabled={submitting || (identification.wants_to_identify && !identification.email)}
              className="w-full bg-[#5AA2AE] hover:bg-[#5AA2AE]/90"
              size="lg"
            >
              {submitting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Submitting...
                </>
              ) : (
                <>
                  Submit Feedback
                  <Send className="ml-2 h-4 w-4" />
                </>
              )}
            </Button>
          </div>
        )}

        {/* Complete Step */}
        {step === 'complete' && (
          <div className="space-y-6 animate-fade-in">
            <Card>
              <CardContent className="py-12 text-center">
                <div className="w-20 h-20 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-6">
                  <CheckCircle2 className="h-10 w-10 text-emerald-600" />
                </div>
                <h2 className="text-2xl font-bold text-slate-900 mb-3" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  Thank You!
                </h2>
                <p className="text-slate-600 max-w-md mx-auto">
                  Your feedback has been submitted successfully. 
                  {identification.wants_to_identify && identification.email && (
                    <span> Please check your email to verify your identity.</span>
                  )}
                </p>
              </CardContent>
            </Card>

            {config.learn_more_url && (
              <a 
                href={config.learn_more_url}
                target="_blank"
                rel="noopener noreferrer"
                className="block"
              >
                <Button variant="outline" className="w-full" size="lg">
                  Learn More about {project.name}
                  <ExternalLink className="ml-2 h-4 w-4" />
                </Button>
              </a>
            )}
          </div>
        )}

        {/* Footer */}
        <div className="text-center text-xs text-slate-400 mt-8 pb-4">
          Pulse by ProjectAI
        </div>
      </div>
    </div>
  );
}
